import type {
  GameLike,
  TeamRef,
} from '@sportninja/common/features/game/gameListView/types'
import { useScheduleTeams } from '@sportninja/common/features/game/gameForm/hooks/useGameOptions'

export interface UseTeamOptionsResult {
  /** uid → team, for the Home / Visitor editors and the patch planner. */
  teamsById: Map<string, TeamRef>
  /** True while the pool is in flight, so the editor can say so. */
  isLoading: boolean
}

const addTeam = (map: Map<string, TeamRef>, team?: TeamRef | null) => {
  if (team?.id == null || team.id === '') {
    return
  }
  map.set(team.id, team)
}

/**
 * The teams the Home and Visitor cells can assign.
 *
 * `/schedules/:id/teams/simple` is the same read the Add/Edit Game modal uses,
 * and the only one that reports `is_placeholder` — which the patch planner
 * needs in order to pin a division rather than take a 400 from an API that
 * cannot derive one.
 *
 * UNIONED with the teams already on loaded games, for the reason
 * `useOfficialOptions` unions its pool: a team the endpoint does not return
 * still has to render its NAME rather than a bare uid. Order matters — the
 * games go in FIRST so the endpoint's entry wins where both know a team, since
 * only that one carries the placeholder flag.
 *
 * A hook of its own rather than a few lines in `DetailsPanel`, so the compiler
 * can hold the map stable across renders. A fresh `Map` on every render would
 * rebuild every column definition underneath it.
 */
export const useTeamOptions = (
  scheduleId: string | null | undefined,
  loadedGames: GameLike[] = []
): UseTeamOptionsResult => {
  'use memo'

  const { teams, isLoading } = useScheduleTeams(scheduleId)

  const teamsById = new Map<string, TeamRef>()

  for (const game of loadedGames) {
    addTeam(teamsById, game.homeTeam)
    addTeam(teamsById, game.visitingTeam)
  }
  for (const team of teams) {
    addTeam(teamsById, team)
  }

  return { teamsById, isLoading }
}
